import { Box } from '@mui/material';
import { useEffect, useRef } from 'react';

interface WiredWeaveProps {
  /** Longitudinal strands running along the wave length (warp). Default: 32 */
  strands?: number;
  /** Transverse ribs crossing perpendicular to create the weave (weft). Default: 70 */
  ribs?: number;
  /** Speed of the wave undulation. Default: 1.0 */
  speed?: number;
  /** Wave height multiplier. Default: 1.0 */
  amplitude?: number;
  /** Width / lateral spread of the weave. Default: 1.0 */
  spread?: number;
  /** Camera distance. Default: 25 */
  cameraZ?: number;
}

export default function WiredWeave({
  strands = 32,
  ribs = 70,
  speed = 1.0,
  amplitude = 1.0,
  spread = 1.0,
  cameraZ = 25,
}: WiredWeaveProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    const ctx = canvas.getContext('2d', {
      alpha: true,
      desynchronized: true,
    });
    if (!ctx) return undefined;

    const TARGET_FPS = 30;
    const FRAME_INTERVAL = 1000 / TARGET_FPS;

    let targetMouseX = 0;
    let targetMouseY = 0;
    let mouseX = 0;
    let mouseY = 0;
    let camX = 0;
    let camY = 1.6;

    let time = 0;
    let lastRenderTime = performance.now();
    let animationFrameId = 0;
    let isVisible = true;

    const handlePointerMove = (clientX: number, clientY: number) => {
      targetMouseX = (clientX / window.innerWidth - 0.5) * 2;
      targetMouseY = (clientY / window.innerHeight - 0.5) * 2;
    };

    const handleMouseMove = (event: MouseEvent) => {
      handlePointerMove(event.clientX, event.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch) {
        handlePointerMove(touch.clientX, touch.clientY);
      }
    };

    const updateSize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = canvas.parentElement?.clientWidth || window.innerWidth;
      const height = canvas.parentElement?.clientHeight || window.innerHeight;

      const targetW = Math.floor(width * dpr);
      const targetH = Math.floor(height * dpr);

      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
      }
    };

    const resizeObserver = new ResizeObserver(updateSize);
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    updateSize();

    const screenWidth = window.innerWidth;
    const isMobile = screenWidth < 768;
    const activeStrands = isMobile ? Math.min(strands, 24) : strands;
    const activeRibs = isMobile ? Math.min(ribs, 52) : ribs;

    const gridCols = activeRibs + 1;
    const gridRows = activeStrands + 1;
    const totalVerts = gridCols * gridRows;

    const px = new Float32Array(totalVerts);
    const py = new Float32Array(totalVerts);
    const visible = new Uint8Array(totalVerts);

    const animate = (currentTime: number) => {
      if (!isVisible) return;

      animationFrameId = requestAnimationFrame(animate);

      const elapsed = currentTime - lastRenderTime;
      if (elapsed < FRAME_INTERVAL) return;
      lastRenderTime = currentTime - (elapsed % FRAME_INTERVAL);

      time += 0.015 * speed;

      mouseX += (targetMouseX - mouseX) * 0.05;
      mouseY += (targetMouseY - mouseY) * 0.05;

      camX += (mouseX * 1.8 - camX) * 0.05;
      camY += (1.6 - mouseY * 0.9 - camY) * 0.05;

      const width = canvas.width;
      const height = canvas.height;
      if (width === 0 || height === 0) return;

      ctx.clearRect(0, 0, width, height);

      const rotZ = Math.sin(time * 0.2) * 0.03;
      const rotY = Math.sin(time * 0.16) * 0.05;
      const rotX = Math.cos(time * 0.18) * 0.035;

      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const cosZ = Math.cos(rotZ);
      const sinZ = Math.sin(rotZ);

      // --- Responsive Mobile Scaling (Enlarged Mobile View) ---
      const aspect = width / height;
      const fovRad = (44 * Math.PI) / 180;

      // In portrait, scale up fovBase so elements appear ~30% larger
      const fovBase = aspect < 1.0 ? (width / 2) * 1.35 : height / 2;
      const fovScale = fovBase / Math.tan(fovRad / 2);

      // Keep camera close to maintain presence on mobile
      const dynamicCamZ = aspect < 1.0 ? cameraZ * 2.2 : cameraZ;
      // Spread ribbon wider across the narrower screen
      const dynamicSpread = aspect < 1.0 ? spread * 1.15 : spread;
      // Boost wave amplitude so the curve fills vertical mobile space
      const dynamicAmplitude = aspect < 1.0 ? amplitude * 1.3 : amplitude;

      const cx = width / 2;
      const cy = height / 2;

      // 1. Calculate and Project All Grid Vertices
      for (let s = 0; s <= activeStrands; s += 1) {
        const vNorm = s / activeStrands;
        const vOffset = (vNorm - 0.5) * 2;

        for (let r = 0; r <= activeRibs; r += 1) {
          const uNorm = r / activeRibs;
          const u = (uNorm - 0.5) * 2;

          const envelope = Math.sin(uNorm * Math.PI);
          const ribbonTwist = u * Math.PI * 1.8 + time * 0.65;
          const x = u * 28 * dynamicSpread;

          const waveHeight =
            (Math.sin(u * 5.0 - time * 1.6 + vOffset * 0.9) * 1.8 +
              Math.cos(u * 9.5 + time * 1.2) * 0.6 +
              Math.cos(ribbonTwist) * vOffset * 4.2) *
            envelope *
            dynamicAmplitude;

          const waveDepth =
            (Math.sin(ribbonTwist) * vOffset * 5.4 +
              Math.sin(u * 3.8 - time * 1.1) * 2.2) *
            envelope;

          const y1 = waveHeight * cosX - waveDepth * sinX;
          const z1 = waveHeight * sinX + waveDepth * cosX;

          const x2 = x * cosY + z1 * sinY;
          const z2 = -x * sinY + z1 * cosY;

          const x3 = x2 * cosZ - y1 * sinZ;
          const y3 = x2 * sinZ + y1 * cosZ;

          const relX = x3 - camX;
          const relY = y3 - camY + 0.25;
          const relZ = dynamicCamZ - z2;

          const idx = s * gridCols + r;

          if (relZ <= 0.1) {
            visible[idx] = 0;
            continue;
          }

          px[idx] = cx + (relX / relZ) * fovScale;
          py[idx] = cy - (relY / relZ) * fovScale;
          visible[idx] = 1;
        }
      }

      ctx.globalCompositeOperation = 'screen';

      // 2. Draw Longitudinal Wires (Warp) - Slightly thicker for crisp visibility on mobile
      ctx.lineWidth = aspect < 1.0 ? 1.4 : 1.25;
      for (let s = 0; s <= activeStrands; s += 1) {
        const rowStart = s * gridCols;
        const rowEnd = rowStart + activeRibs;

        if (!visible[rowStart] || !visible[rowEnd]) continue;

        const x1 = px[rowStart]!;
        const y1 = py[rowStart]!;
        const x2 = px[rowEnd]!;
        const y2 = py[rowEnd]!;

        const grad = ctx.createLinearGradient(x1, y1, x2, y2);
        grad.addColorStop(0.0, 'rgba(255, 255, 255, 0)');
        grad.addColorStop(0.12, 'rgba(34, 211, 238, 0.55)');
        grad.addColorStop(0.5, 'rgba(99, 102, 241, 0.75)');
        grad.addColorStop(0.88, 'rgba(236, 72, 153, 0.65)');
        grad.addColorStop(1.0, 'rgba(255, 255, 255, 0)');

        ctx.strokeStyle = grad;
        ctx.beginPath();
        let isDrawing = false;

        for (let r = 0; r <= activeRibs; r += 1) {
          const idx = s * gridCols + r;
          if (visible[idx]) {
            if (!isDrawing) {
              ctx.moveTo(px[idx]!, py[idx]!);
              isDrawing = true;
            } else {
              ctx.lineTo(px[idx]!, py[idx]!);
            }
          } else {
            isDrawing = false;
          }
        }
        ctx.stroke();
      }

      // 3. Draw Transverse Cross-Wires (Weft)
      ctx.lineWidth = aspect < 1.0 ? 1.0 : 0.9;
      for (let r = 0; r <= activeRibs; r += 2) {
        const rNorm = r / activeRibs;
        const colTop = r;
        const colBottom = activeStrands * gridCols + r;

        if (!visible[colTop] || !visible[colBottom]) continue;

        const x1 = px[colTop]!;
        const y1 = py[colTop]!;
        const x2 = px[colBottom]!;
        const y2 = py[colBottom]!;

        const ribAlpha = Math.sin(rNorm * Math.PI) * 0.45;
        const grad = ctx.createLinearGradient(x1, y1, x2, y2);
        grad.addColorStop(0.0, 'rgba(255, 255, 255, 0)');
        grad.addColorStop(0.5, `rgba(167, 139, 250, ${ribAlpha})`);
        grad.addColorStop(1.0, 'rgba(255, 255, 255, 0)');

        ctx.strokeStyle = grad;
        ctx.beginPath();
        let isDrawing = false;

        for (let s = 0; s <= activeStrands; s += 1) {
          const idx = s * gridCols + r;
          if (visible[idx]) {
            if (!isDrawing) {
              ctx.moveTo(px[idx]!, py[idx]!);
              isDrawing = true;
            } else {
              ctx.lineTo(px[idx]!, py[idx]!);
            }
          } else {
            isDrawing = false;
          }
        }
        ctx.stroke();
      }

      ctx.globalCompositeOperation = 'source-over';

      if (prefersReducedMotion) {
        cancelAnimationFrame(animationFrameId);
      }
    };

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        isVisible = entry.isIntersecting;
        if (isVisible && !prefersReducedMotion) {
          lastRenderTime = performance.now();
          cancelAnimationFrame(animationFrameId);
          animationFrameId = requestAnimationFrame(animate);
        } else {
          cancelAnimationFrame(animationFrameId);
        }
      },
      { threshold: 0.05 }
    );
    intersectionObserver.observe(canvas);

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });

    let idleTimer: number | null = null;
    let fallbackTimer: number | null = null;

    const startAnimation = () => {
      animationFrameId = requestAnimationFrame(animate);
    };

    const win = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        opt?: { timeout: number }
      ) => number;
      cancelIdleCallback?: (id: number) => void;
    };

    if (typeof win.requestIdleCallback === 'function') {
      idleTimer = win.requestIdleCallback(startAnimation, { timeout: 1000 });
    } else {
      fallbackTimer = window.setTimeout(startAnimation, 200);
    }

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (idleTimer !== null && typeof win.cancelIdleCallback === 'function') {
        win.cancelIdleCallback(idleTimer);
      }
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [strands, ribs, speed, amplitude, spread, cameraZ]);

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        contain: 'strict',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          willChange: 'transform',
        }}
      />
      {/* Reduced overlay width on mobile so edges don't eat into the enlarged wave */}
      <Box
        sx={{
          position: 'absolute',
          inset: '0 auto 0 0',
          width: { xs: '6%', sm: '10%', md: '10%' },
          background:
            'linear-gradient(to right, #ffffff 15%, rgba(255, 255, 255, 0) 100%)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: '0 0 0 auto',
          width: { xs: '6%', sm: '10%', md: '10%' },
          background:
            'linear-gradient(to left, #ffffff 15%, rgba(255, 255, 255, 0) 100%)',
        }}
      />
    </Box>
  );
}
